<template>
  <div class="page-annuaire page-annuaire-produits">
    <main class="main">
      <breadcrumbs></breadcrumbs>

      <div class="page_title">
        <h1>Produits</h1>
      </div>

      <div class="main-content">
        <div class="col-100 filtre filtre-produits" v-on:keydown.enter="filter">
          <h2 class="font-bold">Rechercher les produits</h2>
          <div class="labels items-start">
            <SuggesterInput v-on:update:suggesterState="suggesterState = $event" ressource="products" fieldname="name"
              verbose="Nom du produit" v-model="searchInput.name">
            </SuggesterInput>

            <div class="label">
              <label for="productsamm">N° AMM / PCP</label>
              <input v-on:update:suggesterState="suggesterState = $event" type="search" name="productsamm"
                id="productsamm" v-model="searchInput.amm" />
            </div>

            <SuggesterInput v-on:update:suggesterState="suggesterState = $event" ressource="activesubstances"
              fieldname="name" verbose="Substances actives" v-model="searchInput.activesubstance_facets">
            </SuggesterInput>

            <div class="label">
              <label for="type">Type</label>
              <select v-model="searchInput.type" id="type">
                <option value=""></option>
                <option v-for="type in this.types" :key="type">
                  {{ type }}
                </option>
              </select>
            </div>

            <div class="label">
              <label for="productsnatures">Fonction</label>
              <select name="productsnatures" id="productsnatures" v-model="searchInput.natures">
                <option value="" selected></option>
                <option v-for="nature in natures" :key="nature.name" :value="nature.name">
                  {{ nature.name }}
                </option>
              </select>
            </div>

            <SuggesterInput v-on:update:suggesterState="suggesterState = $event" ressource="cultivations"
              fieldname="name" verbose="Culture" select-required v-model="searchInput.cultivations">
            </SuggesterInput>

            <SuggesterInput v-on:update:suggesterState="suggesterState = $event" ressource="targets" fieldname="name"
              verbose="Cible" v-model="searchInput.targets">
            </SuggesterInput>

            <div class="label-checkbox">
              <input id="bio" type="checkbox" v-model="searchInput.logo_ab_use" />
              <label for="bio">Utilisable en agriculture biologique</label>
            </div>

            <div class="label-checkbox">
              <input id="biocontrole" type="checkbox" v-model="searchInput.logo_biocontrole" />
              <label for="biocontrole">Liste biocontrôle</label>
            </div>
            <div class="label-checkbox">
              <input id="amateur" type="checkbox" v-model="searchInput.gamme_amateur" />
              <label for="amateur">Gamme amateur</label>
            </div>
          </div>

          <div class="pt-2">
            <div class="flex justify-end">
              <button class="grey-background" @click="clearSearch">
                Effacer la recherche
              </button>
              <button class="orange-background" @click="filter">
                Lancer la recherche
              </button>
            </div>
            <div class="flex justify-end pt-3">
              <router-link :to="{ name: 'productsAdvancedSearch' }" class="orange-link">
                Accéder à la recherche avancée
              </router-link>
            </div>
          </div>
        </div>

        <div class="col-20 filtre-side">
          <div class="filtre-titre">Substances actives</div>
          <div class="filtre-side-items">
            <div v-for="facet in facets.results" :key="facet.name" class="filtre-side-item">
              <input type="checkbox" :value="facet.name" :id="facet.name" @change="updateFacets"
                v-model="checkedFacets" />
              <label :for="facet.name">{{ facet.name }} ({{ facet.count }})</label>
            </div>
            <template v-if="facets.results.length === queryParams.facets_number">
              <button class="grey-background" @click="
                queryParams.facets_number += 10;
                updateUrl();
              ">
                Voir plus
              </button>
              <div class="grey facet-total">
                Total : {{ facets.total_count }}
              </div>
            </template>
          </div>
        </div>

        <products-listing :products="products" :page.sync="queryParams.page" @changePage="updateUrl()">
        </products-listing>
      </div>
    </main>
  </div>
</template>

<script>
import Nature from "@/api/natures.js";
import Product from "@/api/products.js";
import SearchMixin from "@/mixins/SearchMixin.js";
import SuggesterInput from "@/components/SuggesterInput";
import Breadcrumbs from "@/components/Breadcrumbs";
import ProductsListing from "@/components/ProductsListing";

export default {
  name: "Products",
  components: { SuggesterInput, Breadcrumbs, ProductsListing },
  mixins: [SearchMixin],

  data() {
    return {
      products: {},
      facets: {
        results: [],
      },
      types: [],
      natures: [],
      defaultFacetsNumber: 20,
      queryParams: {
        page: 1,
        activesubstance_facets: [],
      },
      searchInput: {
        name: "",
        amm: "",
        activesubstance_facets: "",
        natures: "",
        cultivations: "",
        targets: "",
        logo_ab_use: false,
        logo_biocontrole: false,
        gamme_amateur: false
      },
      checkedFacets: [],
      suggesterState: false,
      errors: {
        page: null,
      },
    };
  },

  created() {
    this.getNatures().then(() => this.putQueryParamsInSearchInput());

    this.queryParams = { ...this.queryParams, ...this.$route.query };

    if (this.isMobile()) {
      this.defaultFacetsNumber = 8;
    }
    this.getProducts();
    this.getTypes();
  },

  beforeRouteUpdate(to, from, next) {
    this.errors.page = null;
    this.queryParams = { ...to.query };

    if (!to.params.updateFacets) {
      this.putQueryParamsInSearchInput();
    }

    Promise.resolve(this.getProducts()).finally(() => next());
  },

  methods: {
    getProducts() {
      this.queryParams.page = Number(this.queryParams.page) || 1;
      this.queryParams.facets_number =
        Number(this.queryParams.facets_number) || this.defaultFacetsNumber;
      this.handleMultiValuatedQueryParam(["activesubstance_facets"]);
      this.checkedFacets = this.queryParams["activesubstance_facets"] || [];

      return Product.list(this.queryParams).then((response) => {
        this.products = response.products;
        this.facets = response.facets;
      });
    },

    getTypes() {
      return Product.types().then((response) => {
        this.types = response;
      });
    },

    getNatures() {
      return Nature.list().then((response) => {
        this.natures = response;
      });
    },

    filter(event) {
      if (!this.suggesterState || event.type === "click") {
        this.queryParams.page = 1;
        this.queryParams.facets_number = this.defaultFacetsNumber;
        this.queryParams.activesubstance_facets = [];
        this.putSearchInputInQueryParams();
        this.updateUrl({ updateFacets: true });
      }
    },

    clearSearch() {
      for (const prop in this.searchInput) {
        this.searchInput[prop] = "";
      }
      this.putSearchInputInQueryParams();
      this.queryParams.page = 1;
      this.queryParams.facets_number = this.defaultFacetsNumber;
      this.queryParams.activesubstance_facets = [];
      this.updateUrl({ updateFacets: true });
    },

    updateFacets() {
      this.queryParams.page = 1;
      this.queryParams.facets_number = this.defaultFacetsNumber;
      this.queryParams.activesubstance_facets = this.checkedFacets;
      this.updateUrl({ updateFacets: true });
    },

    isMobile() {
      if (screen.width <= 760) {
        return true;
      } else {
        return false;
      }
    },
  },
};
</script>
